/*
 * Full-screen product photo / video gallery: <media-lightbox> in snippets/product-media-lightbox.liquid.
 * Opens from the product gallery (clicking a photo, or the "view full screen" notice button), shows only the
 * media the gallery currently shows (variant filtering), and moves the main gallery to the last item viewed
 * when closed.
 * - Close: "Back to the product", X button, Esc, or clicking the dark area around the photo.
 * - Navigate: arrows, thumbnails, keyboard left / right, or swipe on touch screens (when not zoomed).
 * - Zoom (photos): mouse wheel / trackpad pinch toward the cursor, two-finger pinch or double-tap on touch
 *   screens, keys + - 0. Never smaller than the fitted size; drag to pan when zoomed.
 */
(() => {
  if (!window.customElements || window.customElements.get('media-lightbox')) return;

  const SWIPE_THRESHOLD_PX = 50;
  const MIN_SCALE = 1;
  // Max zoom = up to twice the photo's real resolution, but always at least 4x and never more than 8x.
  const MIN_MAX_SCALE = 4;
  const MAX_MAX_SCALE = 8;
  const MAX_SCALE_PER_NATURAL_PIXEL = 2;
  const DOUBLE_TAP_SCALE = 3;
  const DOUBLE_TAP_MS = 300;
  const TAP_MOVE_TOLERANCE_PX = 10;
  const KEY_ZOOM_FACTOR = 1.25;

  // ---------- Pure helpers ----------

  // Wraps an index into 0..length-1 so the gallery loops at both ends.
  function wrapIndex(index, length) {
    return ((index % length) + length) % length;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isSafeEmbedUrl(value) {
    try {
      return new URL(value).protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  // Keeps a zoomed image covering its frame: no panning past the image edges; centred when not zoomed.
  function clampTranslation(transform, bounds) {
    const maxX = Math.max(0, (bounds.imageWidth * transform.scale - bounds.frameWidth) / 2);
    const maxY = Math.max(0, (bounds.imageHeight * transform.scale - bounds.frameHeight) / 2);
    return { scale: transform.scale, x: clamp(transform.x, -maxX, maxX), y: clamp(transform.y, -maxY, maxY) };
  }

  // Scales around a focal point (relative to the image centre) so the point under it stays in place.
  function zoomAroundPoint(transform, nextScale, focal, bounds) {
    const ratio = nextScale / transform.scale;
    return clampTranslation(
      { scale: nextScale, x: focal.x - ratio * (focal.x - transform.x), y: focal.y - ratio * (focal.y - transform.y) },
      bounds
    );
  }

  // ---------- Photo zoom (one instance per open gallery stage) ----------

  class ImageZoom {
    constructor(stage, { onSwipe, onGestureMoved }) {
      this.stage = stage;
      this.onSwipe = onSwipe;
      this.onGestureMoved = onGestureMoved;
      this.image = null;
      this.transform = { scale: 1, x: 0, y: 0 };
      this.pointers = new Map();
      this.gesture = null;
      this.lastTap = null;
    }

    attach(signal) {
      const options = { signal };
      this.stage.addEventListener('wheel', (event) => this.onWheel(event), { signal, passive: false });
      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event), options);
      this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event), options);
      this.stage.addEventListener('pointerup', (event) => this.onPointerEnd(event), options);
      this.stage.addEventListener('pointercancel', (event) => this.onPointerEnd(event), options);
    }

    setImage(image) {
      this.reset();
      this.image = image || null;
      this.upgradedResolution = false;
    }

    get isZoomed() {
      return this.transform.scale > MIN_SCALE;
    }

    get maxScale() {
      if (!this.image || !this.image.offsetWidth || !this.image.naturalWidth) return MIN_MAX_SCALE;
      const naturalScale = (this.image.naturalWidth / this.image.offsetWidth) * MAX_SCALE_PER_NATURAL_PIXEL;
      return clamp(naturalScale, MIN_MAX_SCALE, MAX_MAX_SCALE);
    }

    getBounds() {
      const frame = this.image.parentElement;
      return {
        imageWidth: this.image.offsetWidth,
        imageHeight: this.image.offsetHeight,
        frameWidth: frame.clientWidth,
        frameHeight: frame.clientHeight,
      };
    }

    // Screen point -> offset from the image's (untransformed) centre, which is the frame centre.
    getFocal(clientX, clientY) {
      const rect = this.image.parentElement.getBoundingClientRect();
      return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) };
    }

    zoomTo(scale, focal) {
      if (!this.image) return;
      const nextScale = clamp(scale, MIN_SCALE, this.maxScale);
      this.transform = zoomAroundPoint(this.transform, nextScale, focal || { x: 0, y: 0 }, this.getBounds());
      this.apply();
    }

    zoomBy(factor) {
      this.zoomTo(this.transform.scale * factor);
    }

    reset() {
      this.transform = { scale: 1, x: 0, y: 0 };
      this.pointers.clear();
      this.gesture = null;
      this.apply();
    }

    apply() {
      const { scale, x, y } = this.transform;
      this.stage.classList.toggle('is-zoomed', scale > MIN_SCALE);
      if (!this.image) return;

      this.image.style.transform = scale > MIN_SCALE ? `translate(${x}px, ${y}px) scale(${scale})` : '';
      // Ask the browser for a sharper file from the srcset once the photo is enlarged.
      if (scale > MIN_SCALE && !this.upgradedResolution) {
        this.image.sizes = `${Math.round(this.image.offsetWidth * this.maxScale)}px`;
        this.upgradedResolution = true;
      }
    }

    // ---------- Events ----------

    onWheel(event) {
      // Zoom only from the photo itself (or anywhere once zoomed), so the wheel elsewhere does nothing.
      if (!this.image || (!this.image.contains(event.target) && !this.isZoomed)) return;
      event.preventDefault();

      const pixelDelta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const sensitivity = event.ctrlKey ? 0.01 : 0.0015; // ctrlKey = trackpad pinch
      this.zoomTo(this.transform.scale * Math.exp(-pixelDelta * sensitivity), this.getFocal(event.clientX, event.clientY));
    }

    toggleZoomAt(clientX, clientY) {
      if (this.isZoomed) this.zoomTo(MIN_SCALE);
      else this.zoomTo(DOUBLE_TAP_SCALE, this.getFocal(clientX, clientY));
    }

    onPointerDown(event) {
      if (event.target.closest('button, video, iframe')) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.image) {
        this.startPinch();
        this.pointers.forEach((point, pointerId) => this.capturePointer(pointerId));
      } else if (this.pointers.size === 1) {
        const onImage = this.image && this.image.contains(event.target);
        if (this.isZoomed && onImage) {
          this.capturePointer(event.pointerId);
          this.gesture = {
            type: 'pan',
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            moved: false,
            isTouch: event.pointerType !== 'mouse',
          };
        } else if (event.pointerType !== 'mouse') {
          this.gesture = { type: 'swipe', startX: event.clientX, startY: event.clientY, onImage };
        }
      }
    }

    // Keeps a drag / pinch tracking if the finger or mouse leaves the photo. Only used once a gesture starts:
    // capturing a plain press would retarget its click to the stage.
    capturePointer(pointerId) {
      try {
        this.stage.setPointerCapture(pointerId);
      } catch (error) {
        // Not capturable (e.g. already released); the gesture still works without it.
      }
    }

    startPinch() {
      const [first, second] = [...this.pointers.values()];
      this.gesture = {
        type: 'pinch',
        startDistance: Math.hypot(second.x - first.x, second.y - first.y) || 1,
        startTransform: { ...this.transform },
        startFocal: this.getFocal((first.x + second.x) / 2, (first.y + second.y) / 2),
        moved: false,
      };
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const gesture = this.gesture;
      if (!gesture) return;

      if (gesture.type === 'pinch' && this.pointers.size >= 2) {
        const [first, second] = [...this.pointers.values()];
        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        const focal = this.getFocal((first.x + second.x) / 2, (first.y + second.y) / 2);
        const start = gesture.startTransform;
        const nextScale = clamp((start.scale * distance) / gesture.startDistance, MIN_SCALE, this.maxScale);
        const ratio = nextScale / start.scale;
        // Keeps the pinched point under the fingers, and follows the fingers if they move together.
        this.transform = clampTranslation(
          {
            scale: nextScale,
            x: focal.x - ratio * (gesture.startFocal.x - start.x),
            y: focal.y - ratio * (gesture.startFocal.y - start.y),
          },
          this.getBounds()
        );
        gesture.moved = true;
        this.apply();
      } else if (gesture.type === 'pan') {
        const deltaX = event.clientX - gesture.lastX;
        const deltaY = event.clientY - gesture.lastY;
        gesture.lastX = event.clientX;
        gesture.lastY = event.clientY;
        const travelled = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
        if (travelled > TAP_MOVE_TOLERANCE_PX) gesture.moved = true;
        this.transform = clampTranslation(
          { scale: this.transform.scale, x: this.transform.x + deltaX, y: this.transform.y + deltaY },
          this.getBounds()
        );
        this.apply();
      }
    }

    onPointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      const gesture = this.gesture;

      if (gesture && gesture.type === 'swipe' && event.type === 'pointerup') this.finishSwipe(gesture, event);
      // A touch that didn't move while zoomed is a tap (double-tap zooms back out).
      if (gesture && gesture.type === 'pan' && gesture.isTouch && !gesture.moved && event.type === 'pointerup') {
        this.handleTap(event, true);
      }
      if (gesture && gesture.moved) this.onGestureMoved();

      if (gesture && gesture.type === 'pinch' && this.pointers.size === 1 && this.isZoomed) {
        // One finger lifted: keep panning with the other.
        const [remaining] = [...this.pointers.values()];
        this.gesture = { type: 'pan', lastX: remaining.x, lastY: remaining.y, moved: true };
      } else if (this.pointers.size === 0) {
        this.gesture = null;
        if (this.transform.scale < MIN_SCALE + 0.02) this.zoomTo(MIN_SCALE);
      }
    }

    finishSwipe(gesture, event) {
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;

      if (Math.abs(deltaX) < TAP_MOVE_TOLERANCE_PX && Math.abs(deltaY) < TAP_MOVE_TOLERANCE_PX) {
        this.handleTap(event, gesture.onImage);
        return;
      }
      if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.onGestureMoved();
        this.onSwipe(deltaX < 0 ? 1 : -1);
      }
    }

    // Double-tap on a photo toggles zoom (touch screens only; desktop zooms with the mouse wheel).
    handleTap(event, onImage) {
      const now = Date.now();
      const isDoubleTap = onImage && this.lastTap && now - this.lastTap.time < DOUBLE_TAP_MS
        && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < TAP_MOVE_TOLERANCE_PX * 3;

      if (isDoubleTap) {
        this.lastTap = null;
        this.onGestureMoved();
        this.toggleZoomAt(event.clientX, event.clientY);
      } else {
        this.lastTap = { time: now, x: event.clientX, y: event.clientY };
      }
    }
  }

  // ---------- Gallery ----------

  class MediaLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.gallery = this.closest('.product-gallery');
      if (!this.dialog || !this.gallery) return;

      this.stage = this.querySelector('[data-lightbox-backdrop]');
      this.slides = new Map([...this.querySelectorAll('[data-lightbox-slide]')].map((slide) => [slide.dataset.mediaId, slide]));
      this.thumbs = new Map([...this.querySelectorAll('[data-lightbox-thumb]')].map((thumb) => [thumb.dataset.mediaId, thumb]));
      this.thumbList = this.querySelector('[data-lightbox-thumb-list]');
      this.counterCurrent = this.querySelector('[data-lightbox-current]');
      this.counterTotal = this.querySelector('[data-lightbox-total]');
      this.items = [];
      this.currentIndex = 0;

      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.gallery.addEventListener('click', (event) => this.onGalleryClick(event), { signal });
      this.dialog.addEventListener('click', (event) => this.onDialogClick(event), { signal });
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.dialog.addEventListener('close', () => this.onClose(), { signal });

      this.zoom = new ImageZoom(this.stage, {
        onSwipe: (direction) => (direction > 0 ? this.next() : this.previous()),
        onGestureMoved: () => this.ignoreClickBriefly(),
      });
      this.zoom.attach(signal);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    // ---------- Opening ----------

    onGalleryClick(event) {
      if (this.contains(event.target)) return;

      if (event.target.closest('[data-lightbox-open]')) {
        event.preventDefault();
        this.open(this.getSelectedGalleryMediaId());
        return;
      }

      // The carousel calls preventDefault on the click that ends a swipe; ignore those.
      if (event.defaultPrevented) return;
      const imageCell = event.target.closest('.product-gallery__carousel-item[data-media-type="image"]');
      if (imageCell) this.open(imageCell.dataset.mediaId);
    }

    getSelectedGalleryMediaId() {
      const selectedCell = this.gallery.querySelector('.product-gallery__carousel-item.is-selected');
      return selectedCell ? selectedCell.dataset.mediaId : null;
    }

    // Media ids in gallery order, skipping media hidden by variant filtering.
    collectVisibleMediaIds() {
      const cells = [...this.gallery.querySelectorAll('.product-gallery__carousel-item')];
      const visibleIds = cells
        .filter((cell) => !cell.classList.contains('is-filtered') && this.slides.has(cell.dataset.mediaId))
        .map((cell) => cell.dataset.mediaId);
      return visibleIds.length ? visibleIds : [...this.slides.keys()];
    }

    open(mediaId) {
      this.items = this.collectVisibleMediaIds();
      if (!this.items.length) return;

      this.thumbs.forEach((thumb, id) => {
        thumb.hidden = !this.items.includes(id);
      });
      this.toggleAttribute('data-single', this.items.length < 2);
      this.counterTotal.textContent = String(this.items.length);

      this.dialog.showModal();
      // Focus the gallery itself (not the first button) so no focus ring appears on open; Tab still reaches every control.
      this.dialog.focus({ preventScroll: true });
      document.documentElement.classList.add('is-locked');
      this.show(Math.max(0, this.items.indexOf(mediaId)), false);
    }

    // ---------- Navigation ----------

    show(index, animateThumbs = true) {
      this.currentIndex = wrapIndex(index, this.items.length);
      const currentId = this.items[this.currentIndex];
      const currentSlide = this.slides.get(currentId);

      this.slides.forEach((slide, id) => {
        const isCurrent = id === currentId;
        if (!isCurrent && !slide.hidden) this.deactivateSlide(slide);
        slide.hidden = !isCurrent;
      });
      this.activateSlide(currentSlide);
      this.zoom.setImage(currentSlide.querySelector('.media-lightbox__image'));
      this.preloadNeighbours();

      this.counterCurrent.textContent = String(this.currentIndex + 1);
      this.thumbs.forEach((thumb, id) => {
        const isCurrent = id === currentId;
        thumb.classList.toggle('is-active', isCurrent);
        if (isCurrent) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.scrollThumbIntoView(this.thumbs.get(currentId), animateThumbs);
    }

    next() {
      this.show(this.currentIndex + 1);
    }

    previous() {
      this.show(this.currentIndex - 1);
    }

    scrollThumbIntoView(thumb, animate) {
      if (!thumb || !this.thumbList) return;
      const left = thumb.offsetLeft - (this.thumbList.clientWidth - thumb.clientWidth) / 2;
      this.thumbList.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
    }

    // Lazy images in hidden slides don't load; switch the neighbours to eager so next / previous feel instant.
    preloadNeighbours() {
      if (this.items.length < 2) return;
      [this.currentIndex - 1, this.currentIndex + 1].forEach((index) => {
        const slide = this.slides.get(this.items[wrapIndex(index, this.items.length)]);
        const image = slide && slide.querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      });
    }

    // YouTube / Vimeo players are created when shown and removed when left, which also stops playback.
    activateSlide(slide) {
      const embed = slide && slide.querySelector('[data-embed-src]');
      if (!embed || embed.querySelector('iframe') || !isSafeEmbedUrl(embed.dataset.embedSrc)) return;

      const frame = document.createElement('iframe');
      frame.src = embed.dataset.embedSrc;
      frame.title = embed.dataset.embedTitle || '';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      embed.append(frame);
    }

    deactivateSlide(slide) {
      slide.querySelectorAll('[data-embed-src] iframe').forEach((frame) => frame.remove());
      slide.querySelectorAll('video').forEach((video) => video.pause());
    }

    // ---------- Events ----------

    // A pan, pinch or swipe ends with a click; that click shouldn't close the gallery.
    ignoreClickBriefly() {
      this.ignoreNextClick = true;
      clearTimeout(this.ignoreClickTimer);
      this.ignoreClickTimer = setTimeout(() => {
        this.ignoreNextClick = false;
      }, 400);
    }

    onDialogClick(event) {
      const target = event.target;
      // Swallow only the background click that ends a gesture; buttons always work.
      if (this.ignoreNextClick && !target.closest('button')) {
        this.ignoreNextClick = false;
        return;
      }

      if (target === this.dialog || target.matches('[data-lightbox-backdrop], .media-lightbox__slide')) {
        // Close only when the click really landed beside the photo / video, not on it.
        if (!this.isPointOverCurrentMedia(event.clientX, event.clientY)) this.dialog.close();
      } else if (target.closest('[data-lightbox-close]')) {
        this.dialog.close();
      } else if (target.closest('[data-lightbox-prev]')) {
        this.previous();
      } else if (target.closest('[data-lightbox-next]')) {
        this.next();
      } else {
        const thumb = target.closest('[data-lightbox-thumb]');
        if (thumb) this.show(this.items.indexOf(thumb.dataset.mediaId));
      }
    }

    isPointOverCurrentMedia(clientX, clientY) {
      const slide = this.slides.get(this.items[this.currentIndex]);
      const media = slide && slide.querySelector('.media-lightbox__image, .media-lightbox__video, .media-lightbox__native-video');
      if (!media) return false;

      const rect = media.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    }

    onKeydown(event) {
      const actions = {
        ArrowLeft: () => this.previous(),
        ArrowRight: () => this.next(),
        '+': () => this.zoom.zoomBy(KEY_ZOOM_FACTOR),
        '=': () => this.zoom.zoomBy(KEY_ZOOM_FACTOR),
        '-': () => this.zoom.zoomBy(1 / KEY_ZOOM_FACTOR),
        0: () => this.zoom.zoomTo(MIN_SCALE),
      };
      const action = actions[event.key];
      if (!action) return;
      event.preventDefault();
      action();
    }

    onClose() {
      const currentSlide = this.slides.get(this.items[this.currentIndex]);
      if (currentSlide) this.deactivateSlide(currentSlide);
      this.zoom.setImage(null);
      document.documentElement.classList.remove('is-locked');
      this.syncGallery(this.items[this.currentIndex]);
    }

    // Moves the main gallery to the last item viewed, using its own thumbnail click handler.
    syncGallery(mediaId) {
      if (!mediaId || mediaId === this.getSelectedGalleryMediaId()) return;
      const galleryThumb = this.gallery.querySelector(`.product-gallery__thumbnail[data-media-id="${CSS.escape(mediaId)}"]`);
      if (galleryThumb) galleryThumb.click();
    }
  }

  window.TCMMediaLightbox = { wrapIndex, clampTranslation, zoomAroundPoint };
  window.customElements.define('media-lightbox', MediaLightbox);
})();
